import { useState, useRef, useCallback, useEffect } from 'react';
import {
    VOICE_SAMPLE_RATE,
    VOICE_BUFFER_SIZE,
    downsampleBuffer,
    encodeUserAudio,
    decodeAgentAudio,
} from '../utils/voiceAudioCodec';

/**
 * A browser test call with an agent: microphone in, agent voice out, over the
 * backend's /test-agent-stream socket. No phone call is made.
 * Shared by the Test Agent tab and the quick-start pop-up.
 */
const useAgentTestSession = (agentId) => {
    const ws = useRef(null);
    const timerInterval = useRef(null);

    const streamRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamSourceRef = useRef(null);
    const processorRef = useRef(null);
    const playbackAudioContextRef = useRef(null);
    const playbackTimeRef = useRef(0);
    const playbackSourcesRef = useRef(new Set());
    const sessionActiveRef = useRef(false);

    const [messages, setMessages] = useState([]);
    const [callSummary, setCallSummary] = useState("");
    const [inCall, setInCall] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [callTimer, setCallTimer] = useState("00:00");
    const [secondsElapsed, setSecondsElapsed] = useState(0);
    const [micError, setMicError] = useState("");
    // "MIC_DENIED", "TEST_LIMIT_REACHED", "CONNECTION" or a backend code
    const [errorCode, setErrorCode] = useState("");
    const [timeLimitReached, setTimeLimitReached] = useState(false);
    const [maxDurationSeconds, setMaxDurationSeconds] = useState(null);
    // Tests a trial user has left after the current one; null when unlimited
    const [testsRemaining, setTestsRemaining] = useState(null);

    useEffect(() => {
        if (!inCall) return;
        const minutes = Math.floor(secondsElapsed / 60).toString().padStart(2, "0");
        const seconds = (secondsElapsed % 60).toString().padStart(2, "0");
        setCallTimer(`${minutes}:${seconds}`);
    }, [secondsElapsed, inCall]);

    const startTimer = useCallback(() => {
        setSecondsElapsed(0);
        timerInterval.current = setInterval(() => {
            setSecondsElapsed((prev) => prev + 1);
        }, 1000);
    }, []);

    const resetTimer = useCallback(() => {
        clearInterval(timerInterval.current);
        setCallTimer("00:00");
        setSecondsElapsed(0);
    }, []);

    const stopAgentPlayback = useCallback(async () => {
        playbackSourcesRef.current.forEach((source) => {
            try { source.stop(); } catch (e) { /* already ended */ }
        });
        playbackSourcesRef.current.clear();
        playbackTimeRef.current = 0;

        if (playbackAudioContextRef.current) {
            try { await playbackAudioContextRef.current.close(); } catch (e) { /* ignore */ }
            playbackAudioContextRef.current = null;
        }
    }, []);

    const playAgentAudio = useCallback((base64Audio) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!playbackAudioContextRef.current) {
                playbackAudioContextRef.current = new AudioContextClass({ sampleRate: VOICE_SAMPLE_RATE });
                playbackTimeRef.current = playbackAudioContextRef.current.currentTime;
            }

            const audioContext = playbackAudioContextRef.current;
            const samples = decodeAgentAudio(base64Audio);
            const audioBuffer = audioContext.createBuffer(1, samples.length, VOICE_SAMPLE_RATE);
            audioBuffer.copyToChannel(samples, 0);

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.onended = () => playbackSourcesRef.current.delete(source);

            const startAt = Math.max(audioContext.currentTime, playbackTimeRef.current);
            playbackSourcesRef.current.add(source);
            source.start(startAt);
            playbackTimeRef.current = startAt + audioBuffer.duration;
        } catch (error) {
            console.error("[TestAgent] Playback error:", error);
        }
    }, []);

    const cleanupAudioResources = useCallback(async () => {
        if (processorRef.current) {
            try { processorRef.current.disconnect(); } catch (e) { /* ignore */ }
            processorRef.current.onaudioprocess = null;
            processorRef.current = null;
        }
        if (mediaStreamSourceRef.current) {
            try { mediaStreamSourceRef.current.disconnect(); } catch (e) { /* ignore */ }
            mediaStreamSourceRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            try { await audioContextRef.current.close(); } catch (e) { /* ignore */ }
            audioContextRef.current = null;
        }
        await stopAgentPlayback();
    }, [stopAgentPlayback]);

    const closeSocket = useCallback(() => {
        if (ws.current) {
            try { ws.current.close(); } catch (e) { /* ignore */ }
            ws.current = null;
        }
    }, []);

    const resetSession = useCallback(async () => {
        sessionActiveRef.current = false;
        resetTimer();
        await cleanupAudioResources();
        closeSocket();
        setInCall(false);
        setConnecting(false);
    }, [cleanupAudioResources, closeSocket, resetTimer]);

    const startTest = useCallback(async (dynamicVariables = {}) => {
        setMicError("");
        setErrorCode("");
        setTimeLimitReached(false);
        setMessages([]);
        setCallSummary("");
        setConnecting(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
        } catch (error) {
            console.error("[TestAgent] Microphone error:", error);
            setMicError("Microphone access denied. Please allow microphone access to test this agent.");
            setErrorCode("MIC_DENIED");
            setConnecting(false);
            return;
        }

        ws.current = new WebSocket(`${process.env.REACT_APP_SOCKET_URL}/test-agent-stream`);

        ws.current.onopen = () => {
            ws.current.send(JSON.stringify({
                event: "test_start",
                agentId,
                token: localStorage.getItem("authToken"),
                dynamicVariables,
            }));
        };

        ws.current.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);

                switch (message.event) {
                    case "test_started": {
                        sessionActiveRef.current = true;
                        setConnecting(false);
                        setInCall(true);
                        setMaxDurationSeconds(message.maxDurationSeconds ?? null);
                        setTestsRemaining(message.testsRemaining ?? null);
                        startTimer();

                        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                        const audioContext = new AudioContextClass();
                        audioContextRef.current = audioContext;

                        const source = audioContext.createMediaStreamSource(streamRef.current);
                        const processor = audioContext.createScriptProcessor(VOICE_BUFFER_SIZE, 1, 1);
                        mediaStreamSourceRef.current = source;
                        processorRef.current = processor;

                        processor.onaudioprocess = (audioEvent) => {
                            if (!sessionActiveRef.current || ws.current?.readyState !== WebSocket.OPEN) return;

                            const inputData = audioEvent.inputBuffer.getChannelData(0);
                            const downsampled = downsampleBuffer(inputData, audioContext.sampleRate, VOICE_SAMPLE_RATE);
                            ws.current.send(encodeUserAudio(downsampled));
                        };

                        source.connect(processor);
                        processor.connect(audioContext.destination);
                        break;
                    }

                    case "agent_message":
                        setMessages((prev) => [...prev, { sender: "bot", text: message.text }]);
                        break;

                    case "agent_audio":
                        if (message.audio) playAgentAudio(message.audio);
                        break;

                    case "user_transcript":
                        setMessages((prev) => [...prev, { sender: "user", text: message.text }]);
                        break;

                    case "interruption":
                        stopAgentPlayback();
                        break;

                    case "test_time_limit":
                        setTimeLimitReached(true);
                        break;

                    case "test_session_ended":
                        setCallSummary(message.summary || "No summary available for this test.");
                        resetSession();
                        break;

                    case "error":
                        setMicError(message.message || "Unable to start the test session.");
                        setErrorCode(message.code || "CONNECTION");
                        resetSession();
                        break;

                    default:
                        break;
                }
            } catch (error) {
                console.error("[TestAgent] Error parsing message:", error);
            }
        };

        ws.current.onerror = () => {
            setMicError("Connection error. Please try again.");
            setErrorCode("CONNECTION");
        };
    }, [agentId, playAgentAudio, resetSession, startTimer, stopAgentPlayback]);

    const endTest = useCallback(() => {
        if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ event: "test_end" }));
        } else {
            resetSession();
        }
    }, [resetSession]);

    const startNewTest = useCallback(() => {
        setCallSummary("");
        setMessages([]);
        setTimeLimitReached(false);
    }, []);

    useEffect(() => {
        return () => {
            resetSession();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        messages,
        callSummary,
        inCall,
        connecting,
        callTimer,
        secondsElapsed,
        micError,
        errorCode,
        timeLimitReached,
        maxDurationSeconds,
        testsRemaining,
        startTest,
        endTest,
        startNewTest,
    };
};

export default useAgentTestSession;
